import React from 'react';
import { Container, Row, Col, Button, Card, CardBody } from 'reactstrap';
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // Ensure this matches existing import
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Field, reduxForm } from 'redux-form';
import renderSelectField from 'src/shared/components/form/Select';
import { connect } from 'react-redux';
import MobileCameraCapture from 'src/shared/components/form/MobileCameraCapture';
import { withRouter } from 'react-router-dom';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import PndRestApi from 'src/utils/pnd/PndRestApi';

// ─── Form Validation ────────────────────────────────────────────────────────
const validate = (values) => {
    const errors = {};
    if (!values.rejectionReason) {
        errors.rejectionReason = 'Please select a reason for rejection';
    }
    return errors;
};

const renderField = ({ input, label, type, meta: { touched, error }, disabled }) => (
    <div className="mb-3">
        <label className="form-label fw-bold text-muted small">{label}</label>
        <div className="input-group">
            <input {...input} type={type} placeholder={label} className={`form-control ${touched && error ? 'is-invalid' : ''}`} disabled={disabled} />
        </div>
        {touched && error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
);

const renderTextArea = ({ input, label, meta: { touched, error } }) => (
    <div className="mb-3">
        <label className="form-label fw-bold text-muted small">{label}</label>
        <textarea {...input} placeholder={label} className={`form-control ${touched && error ? 'is-invalid' : ''}`} rows="3" />
        {touched && error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
);

const DeclineOrderPage = ({ handleSubmit, history, location, initialValues }) => {
    // Retrieve order details passed via navigation state
    const order = location.state?.order || { orderNo: 'Unknown', declineCount: 0 };
    const fromHistory = location.state?.fromHistory;
    const backRoute = fromHistory ? '/web_app/security/history' : '/web_app/order/security_approval';

    const [proofFiles, setProofFiles] = React.useState([]);

    const handlePhotoUpdate = (files) => {
        setProofFiles(files);
    };

    const onSubmit = (values) => {
        // Guard: rejectionReason is required
        if (!values.rejectionReason?.value) {
            showNotification("theme-light", "ltr", "Validation Error", "Please select a reason for rejection");
            return;
        }

        // Fallback: use order.orderId, or order.id if orderId is undefined
        const orderId = order.orderId ?? order.id;

        let data = {
            "approvalStatus": 2, // 2 for declined
            "orderId": orderId,
            "declineReasonId": values.rejectionReason.value,
            "remarks": values.remarks,
        }

        PndRestApi.securityApprovalProcess(data, proofFiles).then(response => {
            if (response && response.data && response.data.status === 200) {
                successNofication(
                    "theme-light",
                    "ltr",
                    "SuccessCode: 200",
                    `Order ${order.orderNo} Declined Successfully`
                );
                history.push(backRoute);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    "ErrorCode: 100",
                    response?.data?.message || `Order ${order.orderNo} Decline Failed`
                );
            }
        });
    };

    const [rejectionReasons, setRejectionReasons] = React.useState([]);

    React.useEffect(() => {
        PndRestApi.getOrderDeclinedReasons()
            .then(response => {
                if (response && response.data && response.data.status === 200) {
                    const reasons = response.data.data.map(item => ({
                        value: item.declineReasonId,
                        label: item.declinedReason
                    }));
                    setRejectionReasons(reasons);
                }
            })
            .catch(error => {
                console.error("Error fetching rejection reasons:", error);
            });
    }, []);

    return (
        <Container className="p-0 pt-3" fluid>
            <Card className="border-0 shadow-sm mb-4">
                <div className="position-relative p-3 p-md-4" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5253 100%)', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="d-flex align-items-center justify-content-between text-white">
                        <div className="d-flex align-items-center gap-3">
                            <Button color="link" onClick={() => history.push(fromHistory ? '/web_app/security/history' : '/web_app/order/security_approval')} className="text-white p-0 me-2" style={{ textDecoration: 'none' }}>
                                <ArrowBackIcon />
                            </Button>
                            <div className="bg-white bg-opacity-25 p-2 rounded-circle">
                                <ReportProblemIcon style={{ fontSize: '1.5rem' }} />
                            </div>
                            <div>
                                <h4 className="mb-0 fw-bold">Decline Order</h4>
                                <p className="mb-0 text-white-70 opacity-15" style={{ fontSize: '2rem' }}>Order #{order.orderNo}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <CardBody className="p-3 p-md-4 bg-light">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="bg-white p-3 p-md-4 rounded shadow-sm mb-3">
                            <Row>
                                <Col xs={12} md={6}>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold text-muted small">Reason for Rejecting <span className="text-danger">*</span></label>
                                        <Field
                                            name="rejectionReason"
                                            component={renderSelectField}
                                            options={rejectionReasons}
                                            placeholder="Select Reason"
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Field
                                        name="declineCount"
                                        component={renderField}
                                        label="Decline Count"
                                        type="text"
                                        disabled={true}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <Field
                                        name="remarks"
                                        component={renderTextArea}
                                        label="Additional Remarks"
                                    />
                                </Col>
                            </Row>
                        </div>

                        <div className="bg-white p-3 p-md-4 rounded shadow-sm mb-3">
                            <h6 className="fw-bold text-muted mb-3 border-bottom pb-2">
                                Proof of Rejection
                                <span className="text-muted small fw-normal ms-2">(Optional - Max 10)</span>
                            </h6>
                            <MobileCameraCapture
                                onPhotoCapture={handlePhotoUpdate}
                                label="Take Photo Proof"
                                maxPhotos={10}
                            />
                        </div>


                        <div className="d-flex gap-2 mt-4 justify-content-center">
                            <Button
                                type="button"
                                color="light"
                                size="sm"
                                onClick={() => history.push(backRoute)}
                                className="fw-bold text-muted"
                                style={{ width: '130px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="danger"
                                size="sm"
                                className="fw-bold shadow-sm"
                                style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5253 100%)', border: 'none', width: '130px' }}
                            >
                                Confirm Decline
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
        </Container>
    );
};

const mapStateToProps = (state, ownProps) => {
    // Check if order details are in location state
    const order = ownProps.location.state?.order;
    return {
        initialValues: {
            declineCount: order ? (order.declinedDetails?.length || 0) + 1 : 1
        }
    };
};

export default withRouter(connect(mapStateToProps)(reduxForm({
    form: 'declineOrderPageForm',
    enableReinitialize: true,
    validate,
})(DeclineOrderPage)));
